import { Action as ReduxAction } from 'redux';
import { BackgroundJobAllowedActions } from '../../../../ts/ServerApi/types/BackgroundJobAllowedActions';
import { FlowAllowedActions } from '../../../../ts/ServerApi/types/FlowAllowedActions';
import { IDataRoleActions } from '../../../../ts/ServerApi/types/IDataRoleActions';
import { IDatasourceActions } from '../../../../ts/ServerApi/types/IDatasourceActions';
import { IProjectActions } from '../../../../ts/ServerApi/types/IProjectActions';
import { IViewActions } from '../../../../ts/ServerApi/types/IViewActions';
import { IWorkbookActions } from '../../../../ts/ServerApi/types/IWorkbookActions';
import { AppState } from '../../../state/AppState';


export enum EntityActionNames {
  ActivateSite = 'activate-site',
  CancelBackgroundJob = 'cancelBackgroundJob',
  ChangeExtractDsToLive = 'change_extract_ds_to_live',
  ChangeExtractDsToLiveEmb = 'change_extract_ds_to_live_emb',
  TasksChangeSchedule = 'tasks-change-schedule',
  TasksEdit = 'tasks-edit',
  TasksEditExpectedDuration = 'tasks-edit-expected-duration',
  SubscriptionChangeDataSetting = 'subscription-change-data-setting',
  TasksChangeSubject = 'tasks-change-subject',
  SubscriptionChangeFormat = 'subscription-change-format',
  SubscriptionChangeFrequency = 'subscription-change-frequency',
  CreateExtractsEncrypted = 'create_extracts_encrypted',
  CreateExtractsUnencrypted = 'create_extracts_unencrypted',
  CreateExtracts = 'create_extracts',
  CreateExtractsEmbEncrypted = 'create_extracts_emb_encrypted',
  CreateExtractsEmbUnencrypted = 'create_extracts_emb_unencrypted',
  CreateExtractsEmb = 'create_extracts_emb',
  AddSelf = 'addSelf',
  Edit = 'edit',
  RemoveSelf = 'removeSelf',
  Resume = 'resume',
  Decrypt = 'decrypt',
  Delete = 'delete',
  DeleteCollection = 'delete_collection',
  TasksDelete = 'tasks-delete',
  Download = 'download',
  Move = 'move',
  NewMetric = 'new_metric',
  NewWorkbook = 'new_workbook',
  RefreshExtracts = 'refresh_extracts',
  RemoveExternalContent = 'removeExternalContent',
  Rename = 'rename',
  TasksResumeExtractRefresh = 'tasks-resume-extract-refresh',
  SubscriptionResume = 'subscription-resume',
  RevisionHistory = 'revision_history',
  TasksRunNow = 'tasks-run-now',
  RunNow = 'runNow',
  TasksPriority = 'tasks-priority',
  TasksResume = 'tasks-resume',
  DisableSchedules = 'disableSchedules',
  EditSchedule = 'edit-schedule',
  EnableSchedules = 'enableSchedules',
  SchedulesRunNow = 'schedules-run-now',
  SetCertification = 'setCertification',
  SetContact = 'setContact',
  SetDataQuality = 'setDataQuality',
  SetDescription = 'setDescription',
  SetDisplayTabs = 'setDisplayTabs',
  SetOwner = 'setOwner',
  Permissions = 'permissions',
  SetTags = 'setTags',
  ShareContent = 'shareContent',
  SuspendSite = 'suspend-site',
  UnsubscribeSubscriptions = 'unsubscribe-subscriptions',
  WhoHasSeenThisView = 'who_has_seen_this_view',
  EditDataConnection = 'edit_data_connection',
  EditFlow = 'editFlow',
  EditSiteSettings = 'edit-site-settings',
  Encrypt = 'encrypt',
  ExtractsEncrptionEncrypted = 'extracts_encrption_encrypted',
  ExtractsEncrptionUnencrypted = 'extracts_encrption_unencrypted',
  ExtractsEncrptionAction = 'extracts_encrption_action',
  HideRecommendation = 'hideRecommendation',
  SiteUserSiteRole = 'siteuser-siteRole',
  SiteUserRemove = 'siteuser-remove',
  SiteUserRemoveFromGroup = 'siteuser-remove-group',
  SiteUserEditGroupMembership = 'siteuser-groupMembership',
  SiteUserAuthentication = 'siteuser-authentication',
  SiteUserForcePasswordReset = 'siteuser-forcePasswordUpdate',
  SiteUserResetLoginRateLimiting = 'siteuser-resetLoginRateLimiting',
  ServerUserDeleteUsers = 'serveruser-delete',
  ServerUserEditSiteMembership = 'serveruser-siteMembership',
  ServerUserForcePasswordReset = 'serveruser-forcePasswordUpdate',
  ServerUserResetLoginRateLimiting = 'serveruser-resetLoginRateLimiting',
  SynchronizeGroup = 'sync-group',
  SynchronizeGroups = 'sync-groups',
  UpdateGroupMinimumSiteRole = 'update-group-site-role',
  UpdateGroupGrantLicenseMode = 'update-group-grant-license-mode',
  DeleteGroup = 'delete-group',
  GroupDatabases = 'group-databases',
}

/**
 * An action that can be performed on an entity (content item, user, group, subscription, etc.).
 */
export interface IEntityAction<TItem, TAvailableActions> {
  name: EntityActionNames;
  parentName?: string;
  translateName(targetItems?: TItem[], state?: AppState): string; // A `Messages` function
  isVisible(targetItems: TItem[], state: AppState, availableActions?: TAvailableActions|undefined): boolean;
  isClickable(availableActions: TAvailableActions|undefined, targetItems: TItem[], state: AppState): boolean;
  createReduxAction(targetItems: TItem[]): ReduxAction;
  isChecked?: (availableActions: TAvailableActions, targetItems: TItem[], state: AppState) => boolean;
}


export type AvailableActionsForAllContentTypes =
  IWorkbookActions &
  IDatasourceActions &
  IProjectActions &
  IDataRoleActions &
  IViewActions &
  FlowAllowedActions &
  BackgroundJobAllowedActions;

// Until we implement story 752767, which will check for permissions on actions of multiple content
// types, we're pretending the user has permission for all actions, so we pass this object into
// the entity actions, instead of the actual available actions from the server for a particular entity.
export const AllActionsAllowed: { readonly [K in keyof AvailableActionsForAllContentTypes]: true } = {
  addComment: true,
  cancelBackgroundJob: true,
  createProject: true,
  decryptExtracts: true,
  del: true,
  download: true,
  encryptExtracts: true,
  launchWebAuthoring: true,
  manageFlow: true,
  refreshExtract: true,
  runFlow: true,
  seeComments: true,
  setConnection: true,
  setCertification: true,
  setDescription: true,
  setDisplayTabs: true,
  setName: true,
  setOwner: true,
  setPermissions: true,
  setProject: true,
  setTags: true,
  showNlp: true,
  versionManagement: true,
  setNlpSetting: true,
  updateRemoveDataQuality: true,
  sendEmail: true,
  createExtracts: true,
  extractsToLive: true,
  webEditFlow: true,
};
